import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AddCardOutlinedIcon from '@mui/icons-material/AddCardOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SearchIcon from '@mui/icons-material/Search';
import API from '../data/api';
import { getUserInfo } from '../utils/authUtil';

const productModules = [
  {
    title: 'ML Models',
    description: 'Subscribe to predictive models for demand, variance, and operational risk.',
    tags: ['Forecasting', 'Risk Scoring'],
  },
  {
    title: 'Data Statistics',
    description: 'Enable structured depot analytics with operational and financial trend packs.',
    tags: ['KPIs', 'Variance Metrics'],
  },
  {
    title: 'Reporting',
    description: 'Manage subscriptions for scheduled reports and executive dashboard snapshots.',
    tags: ['Scheduled Reports', 'Exports'],
  },
];

const PREVIEW_INVOICES = [
  {
    id: 'preview-1',
    invoice_number: 'HB-SUB-2026-0008',
    issued_to_name: 'Leslie Ontong',
    issued_to_email: 'b+customs@stackcards.co.za',
    plan_name: 'ERP Pro',
    billing_period: 'Jan 2026',
    amount: 50,
    currency: 'USD',
    status: 'paid',
    issued_at: '2026-01-01',
    due_at: '2026-01-05',
    paid_at: '2026-01-02',
    payment_method: 'Visa',
  },
  {
    id: 'preview-2',
    invoice_number: 'HB-SUB-2026-0009',
    issued_to_name: 'Leslie Ontong',
    issued_to_email: 'b+customs@stackcards.co.za',
    plan_name: 'ERP Team',
    billing_period: 'Feb 2026',
    amount: 150,
    currency: 'USD',
    status: 'paid',
    issued_at: '2026-02-01',
    due_at: '2026-02-05',
    paid_at: '2026-02-04',
    payment_method: 'Bank Transfer',
  },
  {
    id: 'preview-3',
    invoice_number: 'HB-SUB-2026-0010',
    issued_to_name: 'Mpho Kgosi',
    issued_to_email: 'mpho.kgosi@losdiesel.co.bw',
    plan_name: 'ERP Team',
    billing_period: 'Feb 2026',
    amount: 225,
    currency: 'USD',
    status: 'overdue',
    issued_at: '2026-02-01',
    due_at: '2026-02-05',
    paid_at: null,
    payment_method: 'Card',
  },
  {
    id: 'preview-4',
    invoice_number: 'HB-SUB-2026-0011',
    issued_to_name: 'Boitumelo Phetla',
    issued_to_email: 'boitumelo@stackcards.co.za',
    plan_name: 'ERP Business',
    billing_period: 'Mar 2026',
    amount: 480,
    currency: 'USD',
    status: 'due',
    issued_at: '2026-03-01',
    due_at: '2026-03-05',
    paid_at: null,
    payment_method: 'Card',
  },
];

const PAYMENT_METHOD_BRANDS = ['Visa', 'Mastercard', 'American Express', 'Bank Transfer'];

const INITIAL_PAYMENT_METHODS = [
  {
    id: 'pm-1',
    brand: 'Visa',
    last4: '4242',
    expiry: '08/28',
    holderName: 'Leslie Ontong',
    isDefault: true,
  },
  {
    id: 'pm-2',
    brand: 'Mastercard',
    last4: '1189',
    expiry: '11/27',
    holderName: 'Leslie Ontong',
    isDefault: false,
  },
];

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatDate = (value) => {
  if (!value) return '-';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '-';
  return parsed.toLocaleDateString('en-GB', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  });
};

const normalizeStatus = (value) => {
  const status = String(value || '').trim().toLowerCase();
  if (status === 'paid' || status === 'completed' || status === 'settled') return 'Paid';
  if (status === 'overdue' || status === 'late') return 'Overdue';
  if (status === 'due' || status === 'pending' || status === 'unpaid') return 'Due';
  return 'Unknown';
};

const getStatusChipColor = (status) => {
  if (status === 'Paid') return 'success';
  if (status === 'Overdue') return 'error';
  if (status === 'Due') return 'warning';
  return 'default';
};

const normalizeInvoiceRow = (row, index) => ({
  id: row?.id || row?.invoice_id || `${index}`,
  invoiceNumber: row?.invoice_number || row?.invoice_no || row?.number || `HB-SUB-${index + 1}`,
  userName: row?.issued_to_name || row?.user_name || row?.subscriber_name || row?.customer_name || 'Unknown User',
  userEmail: row?.issued_to_email || row?.user_email || row?.email || '-',
  planName: row?.plan_name || row?.subscription_tier || row?.tier || '-',
  billingPeriod: row?.billing_period || row?.period || '-',
  amount: toNumber(row?.amount ?? row?.invoice_total ?? row?.total_amount),
  currency: row?.currency || 'USD',
  status: normalizeStatus(row?.status || row?.payment_status),
  issuedAt: row?.issued_at || row?.created_at || row?.invoice_date,
  dueAt: row?.due_at || row?.due_date,
  paidAt: row?.paid_at || row?.paid_date || row?.payment_date,
  paymentMethod: row?.payment_method || row?.payment_channel || '-',
});

const formatAmount = (amount, currency = 'USD') => {
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(toNumber(amount));
  } catch (error) {
    return `${toNumber(amount).toFixed(2)} ${currency}`;
  }
};

const ensureDefaultMethod = (methods) => {
  if (!methods.length) return [];
  if (methods.some((method) => method.isDefault)) return methods;
  return methods.map((method, index) => ({ ...method, isDefault: index === 0 }));
};

const createInitialBillingInfo = (userInfo) => {
  const name = `${userInfo?.given_name || ''} ${userInfo?.family_name || ''}`.trim();
  return {
    companyName: userInfo?.company_name || 'HoneyBadger Client',
    billingContact: name || userInfo?.preferred_username || 'Account Owner',
    billingEmail: userInfo?.email || '',
    taxNumber: userInfo?.registration_id ? `REG-${userInfo.registration_id}` : '',
    addressLine1: 'Mmamashia Truck Stop',
    country: 'Botswana',
  };
};

export default function ProductsPage() {
  const userInfo = useMemo(() => getUserInfo() || {}, []);
  const initialBillingInfo = useMemo(() => createInitialBillingInfo(userInfo), [userInfo]);

  const [invoiceRows, setInvoiceRows] = useState([]);
  const [invoiceError, setInvoiceError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [paymentMethods, setPaymentMethods] = useState(() => ensureDefaultMethod(INITIAL_PAYMENT_METHODS));
  const [openAddPaymentMethod, setOpenAddPaymentMethod] = useState(false);
  const [newPaymentMethod, setNewPaymentMethod] = useState({
    brand: 'Visa',
    last4: '',
    expiry: '',
    holderName: '',
    makeDefault: false,
  });
  const [newPaymentMethodError, setNewPaymentMethodError] = useState('');

  const [billingInfo, setBillingInfo] = useState(initialBillingInfo);
  const [billingInfoSaved, setBillingInfoSaved] = useState('');

  useEffect(() => {
    const fetchInvoiceHistory = async () => {
      try {
        const response = await API.get('billing/subscription-invoices');
        const rows =
          response?.data?.items || response?.data?.invoices || response?.data?.data || response?.data || [];
        const normalizedRows = Array.isArray(rows) ? rows.map(normalizeInvoiceRow) : [];
        setInvoiceRows(normalizedRows);
      } catch (error) {
        console.error('Failed to fetch subscription invoices, loading preview data:', error);
        setInvoiceRows(PREVIEW_INVOICES.map(normalizeInvoiceRow));
        setInvoiceError('Live billing endpoint unavailable. Showing preview invoice history.');
      }
    };

    fetchInvoiceHistory();
  }, []);

  useEffect(() => {
    setBillingInfo(initialBillingInfo);
  }, [initialBillingInfo]);

  useEffect(() => {
    setPage(0);
  }, [searchTerm, statusFilter]);

  const filteredInvoiceRows = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    return invoiceRows.filter((row) => {
      const matchesStatus = statusFilter === 'all' || row.status.toLowerCase() === statusFilter;
      if (!matchesStatus) return false;
      if (!normalizedSearch) return true;

      return (
        row.invoiceNumber.toLowerCase().includes(normalizedSearch) ||
        row.userName.toLowerCase().includes(normalizedSearch) ||
        row.userEmail.toLowerCase().includes(normalizedSearch) ||
        row.planName.toLowerCase().includes(normalizedSearch) ||
        row.billingPeriod.toLowerCase().includes(normalizedSearch)
      );
    });
  }, [invoiceRows, searchTerm, statusFilter]);

  const paginatedInvoiceRows = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredInvoiceRows.slice(start, start + rowsPerPage);
  }, [filteredInvoiceRows, page, rowsPerPage]);

  const invoiceSummary = useMemo(
    () =>
      invoiceRows.reduce(
        (acc, row) => {
          acc.total += 1;
          if (row.status === 'Paid') acc.paid += 1;
          if (row.status === 'Due') acc.due += 1;
          if (row.status === 'Overdue') acc.overdue += 1;
          acc.totalAmount += toNumber(row.amount);
          return acc;
        },
        { total: 0, paid: 0, due: 0, overdue: 0, totalAmount: 0 },
      ),
    [invoiceRows],
  );

  const resetPaymentMethodForm = () => {
    setNewPaymentMethod({
      brand: 'Visa',
      last4: '',
      expiry: '',
      holderName: '',
      makeDefault: false,
    });
    setNewPaymentMethodError('');
  };

  const handleClosePaymentDialog = () => {
    setOpenAddPaymentMethod(false);
    resetPaymentMethodForm();
  };

  const handleAddPaymentMethod = () => {
    const cleanedLast4 = newPaymentMethod.last4.replace(/\D/g, '').slice(0, 4);
    if (cleanedLast4.length !== 4) {
      setNewPaymentMethodError('Enter a valid last 4 digits.');
      return;
    }
    if (!/^\d{2}\/\d{2}$/.test(newPaymentMethod.expiry)) {
      setNewPaymentMethodError('Expiry should be MM/YY.');
      return;
    }
    if (!newPaymentMethod.holderName.trim()) {
      setNewPaymentMethodError('Card holder name is required.');
      return;
    }

    const method = {
      id: `pm-${Date.now()}`,
      brand: newPaymentMethod.brand,
      last4: cleanedLast4,
      expiry: newPaymentMethod.expiry,
      holderName: newPaymentMethod.holderName,
      isDefault: newPaymentMethod.makeDefault,
    };

    const nextMethods = ensureDefaultMethod(
      [method, ...paymentMethods].map((payment, index) =>
        newPaymentMethod.makeDefault ? { ...payment, isDefault: index === 0 } : payment,
      ),
    );
    setPaymentMethods(nextMethods);
    handleClosePaymentDialog();
  };

  const handleSetDefaultMethod = (id) => {
    setPaymentMethods((previous) =>
      previous.map((method) => ({
        ...method,
        isDefault: method.id === id,
      })),
    );
  };

  const handleDeleteMethod = (id) => {
    setPaymentMethods((previous) => ensureDefaultMethod(previous.filter((method) => method.id !== id)));
  };

  const handleBillingInfoChange = (event) => {
    const { name, value } = event.target;
    setBillingInfo((previous) => ({ ...previous, [name]: value }));
    setBillingInfoSaved('');
  };

  const handleSaveBillingInfo = () => {
    setBillingInfoSaved('Billing information saved.');
  };

  return (
    <>
      <Helmet>
        <title> Products </title>
      </Helmet>

      <Container maxWidth="xl">
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" sx={{ mb: 1 }}>
            Products
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage subscriptions, invoices, payment methods, and billing information.
          </Typography>
        </Box>

        <Grid container spacing={3} sx={{ mb: 3 }}>
          {productModules.map((module) => (
            <Grid item xs={12} md={4} key={module.title}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Stack spacing={2}>
                    <Typography variant="h6">{module.title}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {module.description}
                    </Typography>
                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                      {module.tags.map((tag) => (
                        <Chip key={`${module.title}-${tag}`} size="small" label={tag} />
                      ))}
                    </Stack>
                    <Button variant="outlined" disabled>
                      Subscribe (Coming Soon)
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Stack spacing={2.5}>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', md: 'center' }}
                spacing={1}
              >
                <Box>
                  <Typography variant="h6" sx={{ mb: 0.5 }}>
                    Subscription Invoice History
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Historical HoneyBadger invoices issued for subscriptions and monthly payments.
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                  <Chip size="small" label={`Total: ${invoiceSummary.total}`} />
                  <Chip size="small" color="success" label={`Paid: ${invoiceSummary.paid}`} />
                  <Chip size="small" color="warning" label={`Due: ${invoiceSummary.due}`} />
                  <Chip size="small" color="error" label={`Overdue: ${invoiceSummary.overdue}`} />
                  <Chip size="small" label={`Billed: ${formatAmount(invoiceSummary.totalAmount)}`} />
                </Stack>
              </Stack>

              {invoiceError && <Alert severity="info">{invoiceError}</Alert>}

              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Search invoice number, user, email, plan, period..."
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  select
                  size="small"
                  label="Status"
                  value={statusFilter}
                  onChange={(event) => setStatusFilter(event.target.value)}
                  sx={{ minWidth: { xs: '100%', md: 160 } }}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="paid">Paid</MenuItem>
                  <MenuItem value="due">Due</MenuItem>
                  <MenuItem value="overdue">Overdue</MenuItem>
                </TextField>
              </Stack>

              <Paper variant="outlined" sx={{ width: '100%', overflow: 'hidden' }}>
                <Box sx={{ overflowX: 'auto' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Invoice #</TableCell>
                        <TableCell>User</TableCell>
                        <TableCell>Plan</TableCell>
                        <TableCell>Billing Period</TableCell>
                        <TableCell align="right">Amount</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Issued</TableCell>
                        <TableCell>Due</TableCell>
                        <TableCell>Paid</TableCell>
                        <TableCell>Payment Method</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginatedInvoiceRows.map((row) => (
                        <TableRow key={row.id} hover>
                          <TableCell sx={{ fontWeight: 600 }}>{row.invoiceNumber}</TableCell>
                          <TableCell>
                            <Stack spacing={0.25}>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {row.userName}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {row.userEmail}
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell>{row.planName}</TableCell>
                          <TableCell>{row.billingPeriod}</TableCell>
                          <TableCell align="right">{formatAmount(row.amount, row.currency)}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={row.status}
                              color={getStatusChipColor(row.status)}
                              variant={row.status === 'Unknown' ? 'outlined' : 'filled'}
                            />
                          </TableCell>
                          <TableCell>{formatDate(row.issuedAt)}</TableCell>
                          <TableCell>{formatDate(row.dueAt)}</TableCell>
                          <TableCell>{formatDate(row.paidAt)}</TableCell>
                          <TableCell>{row.paymentMethod}</TableCell>
                        </TableRow>
                      ))}

                      {paginatedInvoiceRows.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                            No invoice records found.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </Box>
                <TablePagination
                  component="div"
                  count={filteredInvoiceRows.length}
                  page={page}
                  onPageChange={(_, newPage) => setPage(newPage)}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={(event) => {
                    setRowsPerPage(parseInt(event.target.value, 10));
                    setPage(0);
                  }}
                  rowsPerPageOptions={[5, 10, 25]}
                />
              </Paper>
            </Stack>
          </CardContent>
        </Card>

        <Grid container spacing={3}>
          <Grid item xs={12} lg={6}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Stack spacing={2}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography variant="h6">Payment Methods</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Add and manage subscription payment methods.
                      </Typography>
                    </Box>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<AddCardOutlinedIcon />}
                      onClick={() => setOpenAddPaymentMethod(true)}
                    >
                      Add Method
                    </Button>
                  </Stack>

                  {paymentMethods.map((method) => (
                    <Paper key={method.id} variant="outlined" sx={{ p: 1.5 }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                        <Stack spacing={0.35}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {method.brand} **** {method.last4}
                            </Typography>
                            {method.isDefault && <Chip size="small" color="primary" label="Default" />}
                          </Stack>
                          <Typography variant="caption" color="text.secondary">
                            {method.holderName} | Exp {method.expiry}
                          </Typography>
                        </Stack>
                        <Stack direction="row" spacing={0.5}>
                          {!method.isDefault && (
                            <Button size="small" onClick={() => handleSetDefaultMethod(method.id)}>
                              Set Default
                            </Button>
                          )}
                          <Button
                            size="small"
                            color="error"
                            startIcon={<DeleteOutlineIcon />}
                            onClick={() => handleDeleteMethod(method.id)}
                          >
                            Remove
                          </Button>
                        </Stack>
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} lg={6}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="h6">Billing Information</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Maintain company and billing contact details.
                    </Typography>
                  </Box>

                  {billingInfoSaved && <Alert severity="success">{billingInfoSaved}</Alert>}

                  <Grid container spacing={1.5}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Company Name"
                        name="companyName"
                        value={billingInfo.companyName}
                        onChange={handleBillingInfoChange}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Billing Contact"
                        name="billingContact"
                        value={billingInfo.billingContact}
                        onChange={handleBillingInfoChange}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Billing Email"
                        name="billingEmail"
                        value={billingInfo.billingEmail}
                        onChange={handleBillingInfoChange}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Tax / VAT Number"
                        name="taxNumber"
                        value={billingInfo.taxNumber}
                        onChange={handleBillingInfoChange}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Billing Address"
                        name="addressLine1"
                        value={billingInfo.addressLine1}
                        onChange={handleBillingInfoChange}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Country"
                        name="country"
                        value={billingInfo.country}
                        onChange={handleBillingInfoChange}
                      />
                    </Grid>
                  </Grid>

                  <Stack direction="row" justifyContent="flex-end">
                    <Button variant="contained" onClick={handleSaveBillingInfo}>
                      Save Billing Information
                    </Button>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>

      <Dialog open={openAddPaymentMethod} onClose={handleClosePaymentDialog} fullWidth maxWidth="sm">
        <DialogTitle>Add Payment Method</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {newPaymentMethodError && <Alert severity="error">{newPaymentMethodError}</Alert>}
            <TextField
              select
              size="small"
              label="Payment Brand"
              value={newPaymentMethod.brand}
              onChange={(event) =>
                setNewPaymentMethod((previous) => ({
                  ...previous,
                  brand: event.target.value,
                }))
              }
            >
              {PAYMENT_METHOD_BRANDS.map((brand) => (
                <MenuItem key={brand} value={brand}>
                  {brand}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              label="Card Holder Name"
              value={newPaymentMethod.holderName}
              onChange={(event) =>
                setNewPaymentMethod((previous) => ({
                  ...previous,
                  holderName: event.target.value,
                }))
              }
            />
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={6}>
                <TextField
                  size="small"
                  fullWidth
                  label="Last 4 Digits"
                  value={newPaymentMethod.last4}
                  inputProps={{ maxLength: 4 }}
                  onChange={(event) =>
                    setNewPaymentMethod((previous) => ({
                      ...previous,
                      last4: event.target.value.replace(/\D/g, '').slice(0, 4),
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  size="small"
                  fullWidth
                  label="Expiry (MM/YY)"
                  value={newPaymentMethod.expiry}
                  inputProps={{ maxLength: 5 }}
                  onChange={(event) =>
                    setNewPaymentMethod((previous) => ({
                      ...previous,
                      expiry: event.target.value.replace(/[^\d/]/g, '').slice(0, 5),
                    }))
                  }
                />
              </Grid>
            </Grid>
            <FormControlLabel
              control={
                <Switch
                  checked={newPaymentMethod.makeDefault}
                  onChange={(event) =>
                    setNewPaymentMethod((previous) => ({
                      ...previous,
                      makeDefault: event.target.checked,
                    }))
                  }
                />
              }
              label="Set as default payment method"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePaymentDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleAddPaymentMethod}>
            Save Method
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
